import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import { YOUTH_CLASSES as Y } from "../data/youthClasses";
import { BOOKS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOK = BOOKS.find((b) => b.id === "you-are-an-artist") || BOOKS[0];

export default function YouthArtClasses() {
  return (
    <div data-testid="youth-classes-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
        <header className="lg:col-span-7">
          <p className="label mb-4">{Y.title}</p>
          <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
            {Y.hero.headline}{" "}
            <span className="italic text-[hsl(var(--ink-soft))]">{Y.hero.headlineItalic}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed text-lg">{Y.hero.sub}</p>
          <a
            href="#interest"
            className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
            data-testid="youth-hero-cta"
          >
            {Y.registration.open ? "Register now" : "Join the interest list"} <ArrowDown className="h-3.5 w-3.5" />
          </a>
          {Y.registration.open && (
            <Link to="/youth-art-classes/register" className="ml-4 inline-flex items-center gap-2 label link-underline" data-testid="youth-hero-register">
              Go to registration <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
          <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]" data-testid="youth-status">
            {Y.registration.open
              ? `${Y.registration.sessionLabel} · ${Y.registration.dates.join(" · ")} · Registration open`
              : Y.status}
          </p>
        </header>
        <dl className="lg:col-span-5 border-t border-border" data-testid="youth-facts">
          {Y.facts.map((f) => (
            <div key={f.k} className="grid grid-cols-[110px_1fr] gap-4 py-3.5 border-b border-border">
              <dt className="label text-[hsl(var(--ink-soft))]">{f.k}</dt>
              <dd className="text-base">{f.v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="youth-learn">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <p className="label mb-3">What students will learn</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
              Real skills, <span className="italic text-[hsl(var(--ink-soft))]">taught the way working artists learn them.</span>
            </h2>
          </div>
          <ul className="md:col-span-7 border-t border-border">
            {Y.learn.map((l) => (
              <li key={l} className="flex items-start gap-4 py-4 border-b border-border">
                <Check className="h-4 w-4 mt-1.5 shrink-0 text-[hsl(var(--accent))]" />
                <span className="serif text-xl md:text-2xl leading-snug">{l}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border" data-testid="youth-weeks">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 max-w-2xl">
            <p className="label mb-3">Four-week pilot</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
              From first sketch <span className="italic text-[hsl(var(--ink-soft))]">to family showcase.</span>
            </h2>
          </header>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {Y.weeks.map((w) => (
              <li key={w.n} className="bg-background p-7 md:p-8 min-h-[200px] flex flex-col justify-between">
                <span className="serif text-3xl text-[hsl(var(--accent))]">{w.n}</span>
                <div>
                  <p className="label text-[hsl(var(--ink-soft))]">Week {Number(w.n)}</p>
                  <p className="serif text-2xl mt-1 leading-tight">{w.title}</p>
                  <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{w.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16" data-testid="youth-typical-saturday">
            <div className="md:col-span-4">
              <p className="label mb-3">A typical Saturday</p>
              <h3 className="serif text-3xl md:text-4xl tracking-tight font-light leading-[1.05]">
                Two hours, <span className="italic text-[hsl(var(--ink-soft))]">start to finish.</span>
              </h3>
            </div>
            <ol className="md:col-span-8 border-t border-border">
              {Y.typicalSaturday.map((s) => (
                <li key={s.t} className="grid grid-cols-[64px_1fr] gap-4 py-3.5 border-b border-border">
                  <span className="label text-[hsl(var(--accent))]">{s.t}</span>
                  <span className="text-base leading-relaxed">{s.v}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-foreground text-background" data-testid="youth-why">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <figure className="lg:col-span-7" data-testid="youth-photo-main">
              <div className="aspect-[3/2] overflow-hidden bg-background/10">
                <img src={Y.photos[0].src} alt={Y.photos[0].alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <figcaption className="mt-3 label text-background/50">{Y.photos[0].caption}</figcaption>
            </figure>
            <div className="lg:col-span-5">
              <p className="label mb-3 text-background/60">Why families choose Joseph</p>
              <ul className="border-t border-background/20">
                {Y.why.map((w) => (
                  <li key={w} className="py-4 border-b border-background/20 serif text-xl md:text-2xl leading-snug">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <figure className="lg:col-span-5" data-testid="youth-photo-secondary">
              <div className="aspect-[3/2] overflow-hidden bg-background/10">
                <img src={Y.photos[1].src} alt={Y.photos[1].alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <figcaption className="mt-3 label text-background/50">{Y.photos[1].caption}</figcaption>
            </figure>
            <a
              href={BOOK.link}
              target="_blank"
              rel="noopener noreferrer"
              className="lg:col-span-7 grid grid-cols-[120px_1fr] md:grid-cols-[160px_1fr] gap-6 items-center group"
              data-testid="youth-book-link"
            >
              <div className="aspect-[3/4] overflow-hidden bg-background/10">
                <img src={BOOK.cover} alt={BOOK.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
              </div>
              <div>
                <p className="label text-background/60">By the instructor</p>
                <p className="serif text-2xl md:text-3xl mt-2 leading-tight">{BOOK.title}</p>
                <p className="mt-3 text-sm text-background/70 leading-relaxed">{BOOK.blurb}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-border" data-testid="youth-parents">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-10 md:mb-14 max-w-2xl">
            <p className="label mb-3">Parent information</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
              The practical <span className="italic text-[hsl(var(--ink-soft))]">details.</span>
            </h2>
          </header>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {Y.parents.map((p) => (
              <div key={p.k} className="border-t border-border py-5">
                <dt className="label mb-2">{p.k}</dt>
                <dd className="text-sm md:text-base text-[hsl(var(--ink-soft))] leading-relaxed">{p.v}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-12 border-t border-border pt-6 flex flex-wrap gap-x-10 gap-y-3" data-testid="youth-extras">
            {Y.extras.map((x) => (
              <li key={x} className="flex items-start gap-3 text-sm md:text-base max-w-md">
                <Check className="h-4 w-4 mt-1 shrink-0 text-[hsl(var(--accent))]" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <InterestForm />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label mb-2 block">{label}</span>
      {children}
    </label>
  );
}

const input =
  "w-full bg-transparent border-b border-border py-2.5 text-base outline-none focus:border-foreground transition-colors";

function InterestForm() {
  const [f, setF] = useState({ parent_name: "", email: "", phone: "", child_age: "", schedule: "", interests: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const valid = f.parent_name.trim() && /.+@.+\..+/.test(f.email) && f.child_age && f.schedule;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await axios.post(`${API}/youth-interest`, { ...f, phone: f.phone || null, interests: f.interests || null });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please email hello@josephgravesjr.com.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="interest" className="border-t border-border bg-[hsl(var(--surface))] scroll-mt-24" data-testid="youth-interest">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="label mb-3">You Are An Artist! Youth Art Studio</p>
          <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
            Join the <span className="italic text-[hsl(var(--ink-soft))]">interest list.</span>
          </h2>
          <p className="mt-5 text-[hsl(var(--ink-soft))] leading-relaxed">
            No payment now. Once the four Saturdays are set, you'll get the dates and a registration link before
            anyone else. If enough families ask for weekday after-school classes, we'll add them.
          </p>
        </div>
        <div className="lg:col-span-7">
          {Y.registration.open && (
            <Link
              to="/youth-art-classes/register"
              className="mb-10 flex items-center justify-between gap-6 border border-foreground p-6 hover:bg-foreground hover:text-background transition-colors group"
              data-testid="youth-register-card"
            >
              <div>
                <p className="label group-hover:text-background/70">{Y.registration.sessionLabel} · Registration open</p>
                <p className="serif text-2xl md:text-3xl mt-1">Register your child — ${Y.registration.price}</p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0" />
            </Link>
          )}
          {done ? (
            <div data-testid="youth-interest-success">
              <div className="inline-flex items-center gap-2 mb-4 text-[hsl(var(--accent))]">
                <Check className="h-4 w-4" />
                <span className="label !text-[hsl(var(--accent))]">You're on the list</span>
              </div>
              <p className="serif text-3xl md:text-4xl tracking-tight font-light leading-tight">
                Thank you. We'll email you the dates as soon as they're set.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-8" noValidate data-testid="youth-interest-form">
              <Field label="Parent name">
                <input required value={f.parent_name} onChange={set("parent_name")} className={input} autoComplete="name" data-testid="youth-parent-name" />
              </Field>
              <Field label="Email">
                <input required type="email" value={f.email} onChange={set("email")} className={input} autoComplete="email" data-testid="youth-email" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={f.phone} onChange={set("phone")} className={input} autoComplete="tel" data-testid="youth-phone" />
              </Field>
              <Field label="Child's age">
                <select required value={f.child_age} onChange={set("child_age")} className={input} data-testid="youth-child-age">
                  <option value="">Select…</option>
                  {Y.form.ages.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </Field>
              <Field label="Preferred schedule">
                <select required value={f.schedule} onChange={set("schedule")} className={input} data-testid="youth-schedule">
                  <option value="">Select…</option>
                  {Y.form.schedules.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="What kind of art does your child want to learn?">
                <input value={f.interests} onChange={set("interests")} className={input} placeholder="Drawing animals, painting, comics…" data-testid="youth-interests" />
              </Field>
              <div className="md:col-span-2 flex items-center justify-between gap-6 pt-2">
                <p className="text-xs text-[hsl(var(--ink-soft))] max-w-sm">Used only to share class dates and registration. Never sold or shared.</p>
                <button
                  type="submit"
                  disabled={!valid || busy}
                  className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-40 transition-opacity whitespace-nowrap"
                  data-testid="youth-interest-submit"
                >
                  {busy ? "Sending…" : "Join the list"}
                  {!busy && <ArrowRight className="h-3.5 w-3.5" />}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
